<template>
  <div>
    <div class="month-card-header">
      <h2 class="h3">
        <Localization message="staffing-table-title" />
      </h2>
      <AlButton
        @click="isNew = true"
        variant="secondary"
        :disabled="!initialised || editDisabled"
      >
        <span v-if="initialised"><i class="fa fa-plus button-icon" /> Add</span>
        <AlLoading v-if="!initialised" size="sm" />
      </AlButton>
    </div>

    <div
      v-if="notEnoughDataForStaffing"
      class="missing-user-template centered-image-and-text"
    >
      <img
        src="../../../assets/Emilia_FirstUse.png"
        alt="Staffing Unavailable"
      />
      <h3>No data</h3>
      <p>
        Hi there! It looks like you have not created User templates yet. Make
        sure you create them so you can add people with the skills you need to
        meet your service level goals.
      </p>

      <AlButton class="bold-text" @click="goToUserTemplates" variant="secondary"
        >Create User template</AlButton
      >
    </div>

    <div>
      <div v-if="isNew && readyForEdit" class="row row-edit">
        <EditStaffingRow
          :configuration="configuration"
          :isNew="true"
          :staffing="editing"
          :templateOptions="templateOptions"
          :teamOptions="teamOptions"
          :tagOptions="tagOptions"
          @cancel="cancel"
          @save="save($event)"
        />
      </div>
      <template v-for="(row, index) in rows">
        <div class="row" :class="{ 'row-edit': editing === row }" :key="index">
          <template v-if="editing !== row">
            <div class="row-data" data-testid="staffing-table-existing">
              <div>{{ formatDate(row.startDate) }}</div>
              <div class="number-field">{{ row.hires }}</div>
              <div>{{ formatName(row.template) }}</div>
              <div>{{ row.timezone }}</div>
              <div>{{ formatName(row.team) }}</div>
              <div>{{ formatList(row.tags) }}</div>
            </div>
            <div class="row-actions">
              <AlButton
                @click="edit(row)"
                v-if="!isNew"
                data-testid="staffing-table-edit-existing"
                id="staffing-table-edit-existing"
                :disabled="!readyForEdit"
                ><i class="fa fa-edit"
              /></AlButton>
              <AlButton
                @click="remove(row)"
                v-if="!isNew"
                variant="warning"
                data-testid="staffing-table-delete-existing"
                id="staffing-table-delete-existing"
                :disabled="!readyForEdit"
                ><i class="fa fa-trash-alt"
              /></AlButton>
            </div>
          </template>
          <template v-if="editing === row">
            <EditStaffingRow
              :configuration="configuration"
              :isNew="false"
              :staffing="editing"
              :templateOptions="templateOptions"
              :teamOptions="teamOptions"
              :tagOptions="tagOptions"
              @cancel="cancel"
              @save="save($event, index)"
            />
          </template>
        </div>
      </template>
    </div>
    <div class="tab-actions">
      <AlButton
        v-if="showUpdateButton"
        :disabled="!hasChanges"
        @click="update"
        data-testid="staffing-tab-update"
        id="staffing-tab-update"
      >
        <AlLoading v-if="saving" size="sm" />
        <span v-if="!saving">Apply</span>
      </AlButton>
    </div>

    <Toast v-model="showAppliedToast" type="info" :duration="5000">
      <template v-slot:title>Staffing Adjusted</template>
      Page will reload automatically in 5 seconds.
    </Toast>
  </div>
</template>

<script>
import { format, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import {
  Localization,
  RosterConfiguration,
  Team,
  UserTemplate,
  Toast,
} from "@agyletime/wfm-vue-common";
import { Al2Button as AlButton, AlLoading } from "@playvox/almendra-components";
import EditStaffingRow from "./EditStaffingTableRow.vue";
import {
  getTagsFromRosters,
  getTeamIdsForRosters,
} from "../../../utils/roster";
import { clone, equals } from "ramda";

export default {
  name: "StaffingTable",
  components: {
    Localization,
    AlButton,
    EditStaffingRow,
    AlLoading,
    Toast,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
  },
  async created() {
    await this.getRosterConfigurations();
    await Promise.all([
      this.getTeamOptions(),
      this.getTags(),
      this.getUserTemplates(),
    ]);

    this.initialised = true;
  },
  data() {
    const rows = clone(this.configuration.staffingAdjustments);
    return {
      initialised: false,
      isNew: false,
      editing: null,
      saving: false,
      teamOptions: [],
      tagOptions: [],
      templateOptions: [],
      rosterConfigs: null,
      rows: rows || [],
      showAppliedToast: false,
      notEnoughModalDismissed: false,
    };
  },
  computed: {
    editDisabled() {
      return this.teamOptions?.length == 0 || this.templateOptions?.length == 0;
    },
    hasChanges() {
      return !equals(this.rows, this.configuration.staffingAdjustments);
    },
    showUpdateButton() {
      return (
        this.rows.length > 0 ||
        this.$route.params.configuration?.staffingAdjustments?.length > 0
      );
    },
    notEnoughDataForStaffing() {
      return this.initialised && this.editDisabled;
    },
    readyForEdit() {
      return this.initialised && !this.editDisabled;
    },
  },
  methods: {
    goToUserTemplates() {
      window.location = "/manage_users#templates";
    },
    save(leave, index) {
      const rows = [...this.rows];
      if (this.isNew) {
        rows.unshift(leave);
      } else {
        rows.splice(index, 1, leave);
      }
      this.rows = rows;

      this.$emit("save", rows);
      this.editing = null;
      this.isNew = false;
    },
    cancel() {
      this.editing = null;
      this.isNew = false;
      this.$emit("cancel");
    },
    edit(row) {
      this.editing = row;
    },
    async update() {
      this.saving = true;
      const config = this.$route.params.configuration;
      config.staffingAdjustments = this.rows;
      await config.update();
      this.saving = false;
      this.showAppliedToast = true;
      setTimeout(() => {
        location.reload();
      }, 5000);
    },
    remove(row) {
      const rows = [...this.rows];
      rows.splice(row.index, 1);
      this.rows = rows;
    },
    formatDate(value) {
      let dateToFormat = utcToZonedTime(value, "UTC");
      if (typeof dateToFormat === "string") {
        dateToFormat = parseISO(value);
      }
      return format(dateToFormat, "yyyy-MM-dd");
    },
    formatList(value = [], defaultText = "") {
      return value.join(", ") || defaultText;
    },
    formatName(value) {
      return value?.name || "";
    },
    async getRosterConfigurations() {
      if (this.rosterConfigs) {
        return this.rosterConfigs;
      }

      // Get the teams that are selected in the given rosters
      const rosterConfigs = await RosterConfiguration.getRosterConfigurations({
        fields: "teams,_id,name,workPlans",
      });
      this.rosterConfigs = rosterConfigs.filter((rc) => {
        return this.configuration.rosterConfigurationId.find(
          (rcid) => rcid._id == rc._id
        );
      });

      return this.rosterConfigs;
    },
    async getTeamOptions() {
      const selectedConfigs = await this.getRosterConfigurations();

      // Get unique team ids in the rosters
      const uniqueTeamIds = getTeamIdsForRosters(selectedConfigs);
      const allTeams = await Team.getTeams({ active: "Y" });

      this.teamOptions = allTeams.filter(
        (t) => uniqueTeamIds.indexOf(t.id) !== -1
      );

      this.hydrateTeams();
    },
    hydrateTeams() {
      this.rows.forEach((row) => {
        row.team =
          this.teamOptions.find((option) => option.id == row.team?.id) ||
          row.team;
      });
    },
    async getTags() {
      const selectedConfigs = await this.getRosterConfigurations();
      this.tagOptions = getTagsFromRosters(selectedConfigs).map((t) => ({
        id: t,
        name: t,
      }));
    },
    async getUserTemplates() {
      const templates = await UserTemplate.getUserTemplates({ active: true });
      this.templateOptions = templates.filter((t) => {
        return (
          t?.availabilities?.weeks?.length > 0 &&
          t?.contract?.employmentCategory &&
          t?.roles?.length > 0 &&
          t?.shiftTemplates?.length > 0
        );
      });
      this.hydrateTemplates();
    },
    hydrateTemplates() {
      this.rows.forEach((row) => {
        row.template =
          this.templateOptions.find(
            (option) => option.id == row.template?._id
          ) || row.template;
      });
    },
    dismissNotEnoughModal() {
      this.notEnoughModalDismissed = true;
    },
  },
};
</script>

<style>
.missing-user-template .bold-text {
  font-weight: bold;
}
</style>

<style lang="scss" scoped>
.month-card-header {
  display: flex;
  align-items: center;

  h2 {
    margin-right: 2rem;
  }
}
.row {
  padding: 1rem;
  border-bottom: 1px solid var(--color-eerie-black-40);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;

  &:not(.row-edit):hover {
    background-color: var(--color-playvox-green-10);

    .row-actions > * {
      opacity: 1;
      pointer-events: all;
    }
  }
}
.row-data {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
  min-height: 44px;

  & > * {
    width: 15%;
    min-width: 10rem;
    margin-top: 1rem;
    flex: 1;
  }

  & > .number-field {
    flex: 0;
  }
}
.row-actions > * {
  margin-right: 0.5rem;
  opacity: 0;
  pointer-events: none;
}
@media (max-width: 899px) {
  .row-data {
    min-width: 100%;
  }
  .row-data > * {
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 650px) {
  .row-data > * {
    min-width: 50%;
  }
  .row-actions > * {
    opacity: 1;
    pointer-events: all;
  }
}

.tab-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.centered-image-and-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 30rem;
  margin: auto;
  text-align: center;

  h3 {
    margin: 0.5rem;
  }

  img {
    max-width: 15em;
  }
}
</style>
